/*!
=========================================================
* Regiform IMS - Chart of Accounts Service
=========================================================
* Service for Chart of Accounts feature
* Makes real API calls to backend with data transformation
=========================================================
*/

import { apiClient } from "./api";
import {
  transformAccountFromAPI,
  transformAccountsFromAPI,
  transformAccountToCreateAPI,
  transformAccountToUpdateAPI,
  transformAccountTypeFromAPI,
  transformAccountTypesFromAPI,
  calculateHierarchyLevels,
  buildAccountTree,
  flattenAccountTree,
} from "./accountTransformers";

/**
 * Account Service - Provides API operations for Chart of Accounts
 * All responses are transformed to match frontend data structure
 */
export const accountService = {
  /**
   * Get all accounts with optional filtering
   * @param {Object} params - Filter parameters { search, accountType, isActive, leaf }
   * @returns {Promise<Array>} Transformed accounts
   */
  getAccounts: async (params = {}) => {
    try {
      // Map frontend filter names to backend query params
      const apiParams = {};
      if (params.accountType) apiParams.type = params.accountType;
      if (params.isActive !== null && params.isActive !== undefined) {
        apiParams.status = params.isActive ? "ACTIVE" : "INACTIVE";
      }
      // leaf=true asks the backend for accounts with no children directly,
      // rather than the frontend inferring "has children" from whether some
      // other account in the (possibly status-filtered) response names it as
      // a parent — that heuristic misses a rollup account whose children are
      // all INACTIVE once the fetch itself is status=ACTIVE (PR #221 review).
      if (params.leaf) apiParams.leaf = true;

      const response = await apiClient.get("/accounts", { params: apiParams });

      if (!Array.isArray(response)) {
        console.warn("Expected array from /accounts, got:", typeof response);
        return [];
      }

      let transformed = transformAccountsFromAPI(response);

      // Client-side text search (backend doesn't support search param)
      if (params.search) {
        const searchLower = params.search.toLowerCase();
        transformed = transformed.filter(
          (acc) =>
            acc.code?.toLowerCase().includes(searchLower) ||
            acc.name?.toLowerCase().includes(searchLower)
        );
      }

      return calculateHierarchyLevels(transformed);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      throw error;
    }
  },

  /**
   * Get accounts in tree structure (hierarchical)
   * @param {Object} params - Filter parameters
   * @returns {Promise<Array>} Root accounts with nested children
   */
  getAccountsTree: async (params = {}) => {
    try {
      const response = await apiClient.get("/accounts/tree/hierarchy", { params });

      if (!Array.isArray(response)) {
        console.warn("Expected array from /accounts/tree/hierarchy, got:", typeof response);
        return [];
      }

      // Transform each account recursively
      const transformTree = (accounts) => {
        if (!Array.isArray(accounts)) return [];

        return accounts.map(account => {
          const transformed = transformAccountFromAPI(account);

          // Recursively transform child accounts
          if (Array.isArray(account.childAccounts) && account.childAccounts.length > 0) {
            transformed.children = transformTree(account.childAccounts);
          }

          return transformed;
        });
      };

      const tree = transformTree(response);
      return tree;
    } catch (error) {
      console.error("Error fetching account tree:", error);
      throw error;
    }
  },

  // Export the full Chart of Accounts hierarchy as a downloadable PDF
  exportAccountTreePdf: () => {
    return apiClient.getBlob("/accounts/tree/hierarchy?type=pdf");
  },

  // Export the full Chart of Accounts hierarchy as a downloadable Excel workbook
  exportAccountTreeExcel: () => {
    return apiClient.getBlob("/accounts/tree/hierarchy?type=excel");
  },

  /**
   * Get single account by ID
   * @param {number} id - Account ID
   * @returns {Promise<Object>} Transformed account
   */
  getAccountById: async (id) => {
    try {
      const response = await apiClient.get(`/accounts/${id}`);
      return transformAccountFromAPI(response);
    } catch (error) {
      console.error(`Error fetching account ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get account by code
   * @param {string} code - Account code
   * @returns {Promise<Object>} Transformed account
   */
  getAccountByCode: async (code) => {
    try {
      const response = await apiClient.get(`/accounts/code/${code}`);
      return transformAccountFromAPI(response);
    } catch (error) {
      console.error(`Error fetching account with code ${code}:`, error);
      throw error;
    }
  },

  /**
   * Create new account
   * @param {Object} data - Account data from form
   * @returns {Promise<Object>} Created account
   */
  createAccount: async (data) => {
    try {
      const requestBody = transformAccountToCreateAPI(data);
      const response = await apiClient.post("/accounts", requestBody);
      return transformAccountFromAPI(response);
    } catch (error) {
      console.error("Error creating account:", error);
      throw error;
    }
  },

  /**
   * Update existing account
   * @param {number} id - Account ID
   * @param {Object} data - Updated account data
   * @returns {Promise<Object>} Updated account
   */
  updateAccount: async (id, data) => {
    try {
      const requestBody = transformAccountToUpdateAPI(data);
      const response = await apiClient.put(`/accounts/${id}`, requestBody);
      return transformAccountFromAPI(response);
    } catch (error) {
      console.error(`Error updating account ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete account
   * @param {number} id - Account ID
   * @returns {Promise<Object>} Success response
   */
  deleteAccount: async (id) => {
    try {
      return await apiClient.delete(`/accounts/${id}`);
    } catch (error) {
      console.error(`Error deleting account ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get root accounts (no parent)
   * @param {Object} params - Filter parameters
   * @returns {Promise<Array>} Root accounts
   */
  getRootAccounts: async (params = {}) => {
    try {
      const response = await apiClient.get("/accounts/roots", { params });

      if (!Array.isArray(response)) {
        console.warn("Expected array from /accounts/roots, got:", typeof response);
        return [];
      }

      const transformed = transformAccountsFromAPI(response);
      return calculateHierarchyLevels(transformed);
    } catch (error) {
      console.error("Error fetching root accounts:", error);
      throw error;
    }
  },

  /**
   * Get child accounts of a parent
   * @param {number} parentId - Parent account ID
   * @param {Object} params - Filter parameters
   * @returns {Promise<Array>} Child accounts
   */
  getChildAccounts: async (parentId, params = {}) => {
    try {
      const response = await apiClient.get(`/accounts/${parentId}/children`, {
        params,
      });

      if (!Array.isArray(response)) {
        console.warn(`Expected array from /accounts/${parentId}/children, got:`, typeof response);
        return [];
      }

      const transformed = transformAccountsFromAPI(response);
      return calculateHierarchyLevels(transformed);
    } catch (error) {
      console.error(`Error fetching children of account ${parentId}:`, error);
      throw error;
    }
  },

  /**
   * Get account type information
   * @param {string} type - Account type code (ASSET, LIABILITY, etc.)
   * @returns {Promise<Object>} Account type with metadata
   */
  getAccountType: async (type) => {
    try {
      const response = await apiClient.get(`/account-types/${type}`);
      return transformAccountTypeFromAPI(response);
    } catch (error) {
      console.error(`Error fetching account type ${type}:`, error);
      throw error;
    }
  },

  /**
   * Get all account types
   * @returns {Promise<Array>} Account types with metadata
   */
  getAccountTypes: async () => {
    try {
      const response = await apiClient.get("/account-types");

      if (!Array.isArray(response)) {
        console.warn("Expected array from /account-types, got:", typeof response);
        return [];
      }

      return transformAccountTypesFromAPI(response);
    } catch (error) {
      console.error("Error fetching account types:", error);
      throw error;
    }
  },

  /**
   * Get account categories
   * @param {Object} params - Filter parameters { type: "ASSET" }
   * @returns {Promise<Array>} Account categories
   */
  getAccountCategories: async (params = {}) => {
    try {
      const response = await apiClient.get("/account-categories", { params });

      if (!Array.isArray(response)) {
        console.warn("Expected array from /account-categories, got:", typeof response);
        return [];
      }

      // Return categories as-is (already in correct format from backend)
      // Transform field names if needed
      return response.map(cat => ({
        id: cat.id,
        categoryName: cat.categoryName,
        accountType: cat.accountType,
        description: cat.description,
        createdAt: cat.createdAt,
        updatedAt: cat.updatedAt,
        createdBy: cat.createdBy,
        updatedBy: cat.updatedBy,
      }));
    } catch (error) {
      console.error("Error fetching account categories:", error);
      throw error;
    }
  },

  /**
   * Get account category by ID
   * @param {number} id - Category ID
   * @returns {Promise<Object>} Category details
   */
  getAccountCategoryById: async (id) => {
    try {
      const response = await apiClient.get(`/account-categories/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching account category ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get account path (from root to account)
   * @param {number} id - Account ID
   * @returns {Promise<string>} Path string (e.g., "1000 > 1100 > 1110")
   */
  getAccountPath: async (id) => {
    try {
      const response = await apiClient.get(`/accounts/${id}/path`);
      return response;
    } catch (error) {
      console.error(`Error fetching path for account ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get account depth (level in hierarchy)
   * @param {number} id - Account ID
   * @returns {Promise<number>} Depth level (0 for root)
   */
  getAccountDepth: async (id) => {
    try {
      const response = await apiClient.get(`/accounts/${id}/depth`);
      return response;
    } catch (error) {
      console.error(`Error fetching depth for account ${id}:`, error);
      throw error;
    }
  },
};
